import ReactDOM from "react-dom";
import { BrowserRouter } from "react-router-dom";
import App from "./App";
import { StyledEngineProvider } from "@mui/material";
import {
  PublicClientApplication,
  EventType,
  AccountInfo,
  AuthenticationResult,
  AuthError,
} from "@azure/msal-browser";
import {
  b2cPolicies,
  msalConfig,
  protectedResources,
} from "./config/authConfig";
import { I18nextProvider } from "react-i18next";
import i18next from "i18next";
import common_de from "./translations/de/common.json";
import common_en from "./translations/en/common.json";
import common_da from "./translations/da/common.json";
import common_es from "./translations/es/common.json";
import common_fr from "./translations/fr/common.json";
import LanguageDetector from "i18next-browser-languagedetector";
import { compareIssuingPolicy } from "./helpers/utils";
import * as Sentry from "@sentry/react";
export const msalInstance = new PublicClientApplication(msalConfig);

i18next.use(LanguageDetector).init({
  detection: {
    convertDetectedLanguage: (lang: string) => {
      return lang.substring(0, 2);
    },
  },
  interpolation: { escapeValue: false }, // React already does escaping
  debug: process.env.NODE_ENV === "development",
  fallbackLng: "en",
  ns: ["common"],
  defaultNS: "common",
  react: { useSuspense: false },
  resources: {
    en: {
      common: common_en, // 'common' is our custom namespace
    },
    de: {
      common: common_de,
    },
    da: {
      common: common_da,
    },
    es: {
      common: common_es,
    },
    fr: {
      common: common_fr,
    },
  },
  load: "languageOnly",
});

const accounts = msalInstance.getAllAccounts();
if (accounts.length > 0) {
  msalInstance.setActiveAccount(accounts[0] as AccountInfo);
}
msalInstance.addEventCallback((event) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
    const payload = event.payload as AuthenticationResult;
    const account = payload.account;
    if (
      compareIssuingPolicy(
        payload.idTokenClaims,
        b2cPolicies.names.signUpSignIn
      )
    ) {
      msalInstance.setActiveAccount(account);
    }

    /**
     * Below we are checking if the user is returning from the reset password flow.
     * If so, we will ask the user to reauthenticate with their new password.
     */
    if (
      compareIssuingPolicy(
        payload.idTokenClaims,
        b2cPolicies.names.forgotPassword
      )
    ) {
      let signUpSignInFlowRequest = {
        authority: b2cPolicies.authorities.signUpSignIn.authority,
        scopes: [...protectedResources.api.scopes],
      };
      msalInstance.loginRedirect(signUpSignInFlowRequest);
    }
  }

  if (event.eventType === EventType.LOGIN_FAILURE) {
    // Check for forgot password error
    // Learn more about AAD error codes at https://docs.microsoft.com/en-us/azure/active-directory/develop/reference-aadsts-error-codes
    if (event.error) {
      if (event.error instanceof AuthError) {
        if (event.error.errorMessage.includes("AADB2C90118")) {
          const resetPasswordRequest = {
            authority: b2cPolicies.authorities.forgotPassword.authority,
            scopes: [],
          };
          msalInstance.loginRedirect(resetPasswordRequest);
        }
      }
    }
  }
});

if (process.env.NODE_ENV === 'production') {
    Sentry.init({
      dsn: "https://6fe290c77492a754b6bbc3da850df6cd@o4507271355564032.ingest.de.sentry.io/4509354280747088",
      // Setting this option to true will send default PII data to Sentry.
      // For example, automatic IP address collection on events
      sendDefaultPii: true
    });
}

ReactDOM.render(
  <I18nextProvider i18n={i18next}>
    <StyledEngineProvider injectFirst>
      <BrowserRouter>
        <App instance={msalInstance} />
      </BrowserRouter>
    </StyledEngineProvider>
  </I18nextProvider>,
  document.querySelector("#root")
);
